/**
 * Copyright (c) 2018 Honeywell International Inc. All Rights Reserved.
 * Honeywell and its product names are among the trademarks and/or service marks owned by Honeywell International, Inc., or its subsidiaries.
 *
 * For patent information, see http://www.hsmpats.com.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { AppContext } from '@keystone/common';
import APIClient from '../../common/APIClient';

export const VadContext = React.createContext();
export const VadConsumer = VadContext.Consumer;

class VadProvider extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      rules: [],
      results: null,
      selectedRows: [],
      isConfirmDeleteModalOpen: false,
      pdfFileName: null,
    };
  }

  scanVad = async (data) => {
    const { user, setNotification, handleError } = this.context;
    try {
      const results = await APIClient.scanVad(data, user);
      this.setState({ results: results.response.scanResult });
      this.setState({ pdfFileName: results.response.pdfFileName });
      setNotification(`Scan Completed`, 'success');
    } catch (err) {
      handleError(err);
    }
  };

  home = async () => {
    const { user, handleError } = this.context;
    try {
      await APIClient.home(user.idToken);
    } catch (err) {
      handleError(err);
    }
  };

  getIdToken = () => {
    const { user } = this.context;
    return user.idToken;
  };

  createRule = async (createRuleForm) => {
    const { user, setNotification, handleError } = this.context;
    try {
      await APIClient.createRule(createRuleForm, user);
      setNotification(`Rule ${createRuleForm.matchingString} was created.`, 'success');
    } catch (err) {
      handleError(err);
    }
  };

  createSLA = async (createSLAForm) => {
    const { user, setNotification, handleError } = this.context;
    try {
      await APIClient.createSLA(createSLAForm, user);
      setNotification(`SLA created for customer ${createSLAForm.customerName}`, 'success');
    } catch (err) {
      handleError(err);
    }
  };

  fetchAllSLAs = async () => {
    const { user, handleError } = this.context;
    try {
      const SLAs = await APIClient.fetchAllSLAs(user);
      return SLAs.response;
    } catch (err) {
      return handleError(err);
    }
  };

  fetchRules = async () => {
    const { user, handleError } = this.context;
    try {
      const rules = await APIClient.fetchRules(user);
      this.setState({ rules: rules.response });
    } catch (err) {
      handleError(err);
    }
  };

  editRule = async (editRuleForm) => {
    const { user, setNotification, handleError } = this.context;
    try {
      await APIClient.editRule(editRuleForm, user);
      setNotification(`User ${editRuleForm.matchingString} was modified.`, 'success');
      this.setSelectedRows([]);
    } catch (err) {
      handleError(err);
    }
  };

  deleteRules = async () => {
    const { selectedRows, isConfirmDeleteModalOpen } = this.state;
    const { user, setNotification, handleError } = this.context;
    console.log('perform API call to delete the following rules...');
    console.log(selectedRows);
    try {
      let message = '';
      let userIds = '';
      let operCount = 0;
      const ruleArray = selectedRows;
      if (ruleArray.length > 1) {
        ruleArray.forEach((rule) => {
          if (operCount > 0) {
            userIds = `${userIds}##${rule.id}`;
          } else {
            userIds = rule.id;
          }
          operCount += 1;
        });
        message = 'Rules deleted successfully.';
      } else {
        userIds = ruleArray[0].id;
        message = 'Rule deleted successfully.';
      }
      await APIClient.deleteRules(userIds, user);
      this.setState({
        isConfirmDeleteModalOpen: !isConfirmDeleteModalOpen,
        selectedRows: [],
      });
      setNotification(message, 'success');
    } catch (err) {
      this.setState({
        isConfirmDeleteModalOpen: !isConfirmDeleteModalOpen,
        selectedRows: [],
      });
      handleError(err);
    }
  };

  toggleConfirmDeleteUserModal = () => {
    const { isConfirmDeleteModalOpen } = this.state;
    this.setState({
      isConfirmDeleteModalOpen: !isConfirmDeleteModalOpen,
    });
  };

  setSelectedRows = (selectedRows) => {
    this.setState({ selectedRows });
  };

  resetResults = (results) => {
    this.setState({ results });
  };

  getUser = () => {
    const { user } = this.context;
    return user;
  };

  render() {
    const { children } = this.props;
    const {
      results,
      selectedRows,
      rules,
      history,
      isConfirmDeleteModalOpen,
      pdfFileName,
    } = this.state;
    return (
      <VadContext.Provider
        value={{
          // state vars
          results,
          selectedRows,
          rules,
          history,
          isConfirmDeleteModalOpen,
          pdfFileName,

          // api methods
          scanVad: this.scanVad,
          getIdToken: this.getIdToken,
          createRule: this.createRule,
          editRule: this.editRule,
          fetchRules: this.fetchRules,
          fetchHistory: this.fetchHistory,
          deleteRules: this.deleteRules,
          toggleConfirmDeleteUserModal: this.toggleConfirmDeleteUserModal,
          setSelectedRows: this.setSelectedRows,
          resetResults: this.resetResults,
          home: this.home,
          getUser: this.getUser,
          fetchAllSLAs: this.fetchAllSLAs,
          createSLA: this.createSLA,
        }}
      >
        {children}
      </VadContext.Provider>
    );
  }
}

VadProvider.contextType = AppContext;

VadProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default VadProvider;
